import { docFieldIsAssociationTable } from "building-types/associations/associationTables";
import type { DocEntityAssociation } from "building-types/associations/associationTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { Table, RWTable } from "common/data/table";
import deepequal from "common/utils/deepequal";
import shallowCompare from "common/utils/shallowCompare";
import { resolveOptionsInFormula, evalFormulas } from "doc/formula/evalFormulas";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import memo from "../utils/memo";

/*
 * TODO: Investigate if we want to split this generation stage into different ones,
 * each one of them handling related associations. This split could help in avoiding calling
 * again generation stages not really affected by an association chagne in something not related
 * with that building system.
 * For example, we could have a separate stage for all the associati1on invonlving facades
 * (associations with a facade as a left or right entity).
 */

function select({ doc, optionChoices }: Building) {
  const definitions = docToOptionDefinitions(doc);
  // get all the doc tables that are association tables
  const associationTables = Object.keys(docFieldIsAssociationTable).reduce(
    (acc, tableName) => {
      acc[tableName] = doc[tableName];
      return acc;
    },
    {} as RWTable<Table<DocEntityAssociation>>,
  );

  // resolve the options choices for each association table
  const optionsChoicesTable = Object.keys(associationTables).reduce((acc, tableName) => {
    acc[tableName] = resolveOptionsInFormula(doc[tableName], definitions, optionChoices);
    return acc;
  }, {} as RWTable<OptionChoices>);

  return {
    associationTables,
    optionsChoicesTable,
  };
}

function compare(a: ReturnType<typeof select>, b: ReturnType<typeof select>) {
  if (!a || !b) {
    return false;
  }

  for (const k in a) {
    // shallow compare for optionsChoicesTable
    if (k === "optionsChoicesTable") {
      const aa = a[k];
      const bb = b[k];
      const oldPropNames = Object.getOwnPropertyNames(aa);
      const newPropNames = Object.getOwnPropertyNames(bb);

      if (oldPropNames.length !== newPropNames.length) {
        return false;
      }

      for (const id of oldPropNames) {
        if (!aa[id]) {
          return false;
        }

        if (!bb[id]) {
          return false;
        }

        return shallowCompare(aa[id], bb[id]);
      }
      // deep compare for the rest
    } else if (!deepequal(a[k], b[k])) {
      return false;
    }
  }

  return true;
}

function generate({
  associationTables,
  optionsChoicesTable,
}: ReturnType<typeof select>): Pick<Building, keyof typeof docFieldIsAssociationTable> {
  // evaluate the formulas for each association table
  const evaluatedAssociationTables = Object.keys(associationTables).reduce(
    (acc, tableName) => {
      acc[tableName] = evalFormulas(associationTables[tableName], (v) => optionsChoicesTable[tableName][v]);
      return acc;
    },
    {} as RWTable<Table<DocEntityAssociation>>,
  );

  // return the filtered association tables
  return Object.keys(evaluatedAssociationTables).reduce(
    (acc, tableName) => {
      acc[tableName] = removeFormulaDisabledAssociation(evaluatedAssociationTables[tableName]);
      return acc;
    },
    {} as Pick<Building, keyof typeof docFieldIsAssociationTable>,
  );
}

function removeFormulaDisabledAssociation(tableItems: Table<DocEntityAssociation>): Table<DocEntityAssociation> {
  const filtered: RWTable<DocEntityAssociation> = {};

  for (const id in tableItems) {
    const item = tableItems[id];
    if (!item) {
      continue;
    }

    if (item.disabled) {
      continue;
    }

    filtered[id] = item;
  }

  return filtered;
}

export default memo(select, generate, compare);
